import { Suspense, lazy, useEffect, useState } from "react";
import type { SubmissionFilter } from "@sga/shared";
import { ProtectedRoute } from "./auth/ProtectedRoute.js";
import { useAuth } from "./auth/AuthContext.js";
import { AppShell, type ShellView } from "./components/AppShell.js";
import { LoginPage } from "./pages/LoginPage.js";
import { TotpChallengePage } from "./pages/TotpChallengePage.js";
import { TotpEnrolPage } from "./pages/TotpEnrolPage.js";
import { DashboardHome } from "./pages/DashboardHome.js";
import { FormsPage } from "./pages/FormsPage.js";
import { FormBuilder } from "./builder/FormBuilder.js";
import { SubmissionsPage } from "./submissions/SubmissionsPage.js";
import { ExportHistory } from "./exports/ExportHistory.js";
import { ScheduledExports } from "./exports/ScheduledExports.js";
import { BrandProfilePage } from "./settings/BrandProfilePage.js";
import { ProgrammesPage } from "./settings/ProgrammesPage.js";
import { UsersPage } from "./settings/UsersPage.js";
import { ProfilePage } from "./settings/ProfilePage.js";
import { DetailDrawer } from "./submissions/DetailDrawer.js";
import type { LoginChallenge } from "./auth/types.js";
import {
  clearPreAuth,
  readPreAuth,
  writePreAuth,
  type PreAuthStep,
} from "./auth/preAuthStorage.js";

/**
 * The analytics screens load on demand.
 *
 * Recharts is around 300 KB of the dashboard's JavaScript and it is the only thing that needs it.
 * Statically imported it went into the single entry chunk, so a coordinator opening the submissions
 * table paid for a charting library they were not going to use — on the same Nigerian mobile
 * networks `apps/public` budgets so carefully for. Split out, the entry chunk drops by more than
 * half and the charts arrive when someone asks for a chart.
 */
const AnalyticsHome = lazy(() =>
  import("./analytics/AnalyticsHome.js").then((module) => ({ default: module.AnalyticsHome })),
);
const FormAnalyticsPage = lazy(() =>
  import("./analytics/FormAnalyticsPage.js").then((module) => ({
    default: module.FormAnalyticsPage,
  })),
);
/**
 * The events section loads on demand for the same reason the analytics screens do: it carries the
 * camera scanner and, on browsers without `BarcodeDetector`, a QR decoding library. A coordinator
 * opening the submissions table should not pay for either.
 */
const EventsPage = lazy(() =>
  import("./events/EventsPage.js").then((module) => ({ default: module.EventsPage })),
);

const BroadcastsPage = lazy(() =>
  import("./broadcasts/BroadcastsPage.js").then((module) => ({ default: module.BroadcastsPage })),
);

const ProgrammeAnalyticsPage = lazy(() =>
  import("./analytics/ProgrammeAnalyticsPage.js").then((module) => ({
    default: module.ProgrammeAnalyticsPage,
  })),
);

/** A form opened for submissions, optionally arriving with a filter already applied. */
interface SubmissionsTarget {
  id: string;
  title: string;
  filters?: SubmissionFilter[];
}

/**
 * No router yet — deliberately. There is nothing to deep-link to until Phase 4's form builder;
 * see docs/04-BUILD-PLAN.md. This state machine covers exactly the screens shipped so far: sign in,
 * TOTP (challenge or first-time enrolment, chosen from the login response), and the app shell.
 */
export const App = (): JSX.Element => {
  // Seeded from session storage, so a reload between the password and the code does not send
  // someone back to the password screen — see preAuthStorage.ts.
  const [preAuth, setPreAuthState] = useState<PreAuthStep>(readPreAuth);

  const setPreAuth = (next: PreAuthStep): void => {
    writePreAuth(next);
    setPreAuthState(next);
  };
  const [view, setView] = useState<ShellView>("forms");
  const [openFormId, setOpenFormId] = useState<string | null>(null);
  const [submissionsForm, setSubmissionsForm] = useState<SubmissionsTarget | null>(null);
  const [analyticsForm, setAnalyticsForm] = useState<{ id: string; title: string } | null>(null);
  const [analyticsProgramme, setAnalyticsProgramme] = useState<{ id: string; name: string } | null>(
    null,
  );
  const [globalSubmissionId, setGlobalSubmissionId] = useState<string | null>(null);

  const handleChallenge = (challenge: LoginChallenge): void => {
    setPreAuth(
      challenge.totpEnrolled
        ? { step: "totp-challenge", challengeId: challenge.challengeId }
        : { step: "totp-enrol", challengeId: challenge.challengeId },
    );
  };

  const backToLogin = (): void => setPreAuth({ step: "login" });

  /**
   * Once a session exists the challenge is spent, and leaving it behind would have the next
   * reload of this tab open on a TOTP screen for a login that already finished.
   */
  const { user } = useAuth();
  useEffect(() => {
    if (user) clearPreAuth();
  }, [user]);

  const navigate = (next: ShellView): void => {
    setView(next);
    setSubmissionsForm(null);
    setAnalyticsForm(null);
    setAnalyticsProgramme(null);
  };

  const fallback =
    preAuth.step === "login" ? (
      <LoginPage onChallenge={handleChallenge} />
    ) : preAuth.step === "totp-challenge" ? (
      <TotpChallengePage challengeId={preAuth.challengeId} onBack={backToLogin} />
    ) : (
      <TotpEnrolPage challengeId={preAuth.challengeId} onBack={backToLogin} />
    );

  // The builder takes over the whole viewport — three panes plus a preview do not fit inside the
  // standard shell, and a coordinator building a form wants the screen.
  if (openFormId) {
    return (
      <ProtectedRoute fallback={fallback}>
        <FormBuilder formId={openFormId} onExit={() => setOpenFormId(null)} />
      </ProtectedRoute>
    );
  }

  const content = submissionsForm ? (
    <SubmissionsPage
      key={`${submissionsForm.id}:${JSON.stringify(submissionsForm.filters ?? [])}`}
      formId={submissionsForm.id}
      formTitle={submissionsForm.title}
      initialFilters={submissionsForm.filters}
      onBack={() => setSubmissionsForm(null)}
    />
  ) : analyticsForm ? (
    <FormAnalyticsPage
      formId={analyticsForm.id}
      formTitle={analyticsForm.title}
      onBack={() => setAnalyticsForm(null)}
      // Clicking a category on a chart opens the submissions behind it. The analytics screen stays
      // in place underneath: "Back" from the table returns to the chart the coordinator came from.
      onDrillDown={(filter) =>
        setSubmissionsForm({
          id: analyticsForm.id,
          title: analyticsForm.title,
          filters: [filter],
        })
      }
    />
  ) : analyticsProgramme ? (
    <ProgrammeAnalyticsPage
      programmeId={analyticsProgramme.id}
      programmeName={analyticsProgramme.name}
    />
  ) : view === "forms" ? (
    <FormsPage
      onOpen={setOpenFormId}
      onOpenSubmissions={setSubmissionsForm}
      onOpenAnalytics={(form) => {
        setView("analytics");
        setAnalyticsForm(form);
      }}
    />
  ) : view === "events" ? (
    <EventsPage />
  ) : view === "broadcasts" ? (
    <BroadcastsPage />
  ) : view === "analytics" ? (
    <AnalyticsHome onOpenForm={setAnalyticsForm} onOpenProgramme={setAnalyticsProgramme} />
  ) : view === "exports" ? (
    <div className="flex flex-col gap-8">
      <ExportHistory />
      <ScheduledExports />
    </div>
  ) : view === "brand" ? (
    <BrandProfilePage />
  ) : view === "programmes" ? (
    <ProgrammesPage />
  ) : view === "users" ? (
    <UsersPage />
  ) : view === "profile" ? (
    <ProfilePage />
  ) : (
    <DashboardHome
      onOpenForm={setOpenFormId}
      onOpenForms={() => setView("forms")}
      onNewForm={() => setView("forms")}
    />
  );

  return (
    <ProtectedRoute fallback={fallback}>
      <AppShell activeView={view} onNavigate={navigate} onOpenSubmission={setGlobalSubmissionId}>
        <Suspense fallback={<p className="text-sm text-muted-foreground">Loading…</p>}>
          {content}
        </Suspense>
        {/* A search hit can land on a submission in a form the user is not currently looking at,
            so the drawer lives at the shell level rather than inside the table. */}
        <DetailDrawer
          submissionId={globalSubmissionId}
          onClose={() => setGlobalSubmissionId(null)}
        />
      </AppShell>
    </ProtectedRoute>
  );
};
